import { GLOBAL_NAV_MODE_DIALOG as DIALOG_MODE, GLOBAL_NAV_MODE_DYNAMIC as DYNAMIC_MODE, GLOBAL_NAV_MODE_PANEL as PANEL_MODE, NAV_STATE, TOGGLE_MODAL_EVENT_NAME, } from "../configuration/constants.js";
import { dispatchToggleModalEvent } from "../configuration/utils.js";
import { MediaQueryController } from "./MediaQueryController.js";
const PANEL_OPEN_CLASS = "atlas-gn-panel--open";
const PANEL_COLLAPSED_CLASS = "atlas-gn-panel--collapsed";
const { Disabled: DISABLED, DialogClosed: DIALOG_CLOSED, DialogOpen: DIALOG_OPEN, PanelCollapsed: PANEL_COLLAPSED, PanelExpanded: PANEL_EXPANDED, } = NAV_STATE;
export class ResponsivePanelController {
    constructor(host, mediaQuery, contentRef, dialog, inlineContainerRef) {
        this.contentRef = contentRef;
        this.dialog = dialog;
        this.inlineContainerRef = inlineContainerRef;
        this.state = NAV_STATE.Disabled;
        this.isEnabled = false;
        this.isOtherModalOpen = false;
        this.hasHostFirstUpdated = false;
        this.handleToggleModalEvent = (event) => {
            if (event.target !== this.host) {
                this.isOtherModalOpen = event.detail.open;
            }
        };
        // Should be called from `willUpdate` in the host component.
        this.updateNavState = ({ displayMode, isOpen, }) => {
            this.state = this.getNavState({
                isFirstUpdateDone: this.hasHostFirstUpdated,
                isSmallScreen: this.isSmallScreen,
                hasNav: this.isEnabled,
                displayMode,
                isOpen,
            });
        };
        (this.host = host).addController(this);
        this.mediaQueryController = new MediaQueryController(host, mediaQuery);
    }
    hostConnected() {
        document.addEventListener(TOGGLE_MODAL_EVENT_NAME, this.handleToggleModalEvent);
    }
    hostDisconnected() {
        document.removeEventListener(TOGGLE_MODAL_EVENT_NAME, this.handleToggleModalEvent);
    }
    hostUpdated() {
        this.hasHostFirstUpdated = true;
        // When the nav state changes, might need to move the navigation to a different container.
        if (this.state.startsWith("dialog")) {
            this.moveElementRef(this.dialog, this.contentRef);
            const dialog = this.dialog.value;
            if (!dialog) {
                return;
            }
            if (this.state === NAV_STATE.DialogOpen) {
                if (this.isOtherModalOpen) {
                    // When other modals are open, we don't want the panel to open above them,
                    // so we'll use the `open` attribute instead of the `showModal` method.
                    dialog.open = true;
                    dispatchToggleModalEvent(dialog, true, "panel");
                }
                else {
                    if (!dialog.open) {
                        dialog.showModal();
                        dispatchToggleModalEvent(dialog, true, "dialog");
                    }
                    else {
                        dialog.close();
                        dispatchToggleModalEvent(dialog, false, "dialog");
                    }
                }
            }
            else if (this.state === NAV_STATE.DialogClosed && dialog.open) {
                // When the dialog is open, but should be closed, we'll close it.
                dialog.close();
                dispatchToggleModalEvent(dialog, false, "dialog");
            }
        }
        else if (this.state.startsWith("panel")) {
            this.moveElementRef(this.inlineContainerRef, this.contentRef);
        }
        this.updateBodyClasses(this.state);
    }
    enable() {
        this.isEnabled = true;
        this.host.requestUpdate();
    }
    disable() {
        this.isEnabled = false;
        this.host.requestUpdate();
    }
    get isSmallScreen() {
        return this.mediaQueryController.matches;
    }
    get isDialog() {
        return this.state === DIALOG_OPEN || this.state === DIALOG_CLOSED;
    }
    getNavState({ isFirstUpdateDone, hasNav, displayMode, isSmallScreen, isOpen, }) {
        if (!hasNav) {
            return DISABLED;
        }
        const isDynamicMode = displayMode === DYNAMIC_MODE;
        const isDialogMode = displayMode === DIALOG_MODE || (isDynamicMode && isSmallScreen);
        const isPanelMode = displayMode === PANEL_MODE || (isDynamicMode && !isSmallScreen);
        if (isDialogMode) {
            // If panel mode is disabled, or we're on a mobile device,
            // we don't want the dialog to open on the first render.
            if (!isFirstUpdateDone) {
                return DIALOG_CLOSED;
            }
            return isOpen ? DIALOG_OPEN : DIALOG_CLOSED;
        }
        if (isPanelMode) {
            return isOpen ? PANEL_EXPANDED : PANEL_COLLAPSED;
        }
        // Fallback, though logically covered by the above conditions.
        return DISABLED;
    }
    moveElementRef(target, element) {
        const nav = element.value;
        if (!nav || !target.value) {
            return;
        }
        if (!target.value.contains(nav)) {
            target.value.appendChild(nav);
        }
    }
    updateBodyClasses(state) {
        const classes = document.body.classList;
        if (state === DISABLED || state === DIALOG_CLOSED || state === DIALOG_OPEN) {
            classes.remove(PANEL_OPEN_CLASS, PANEL_COLLAPSED_CLASS);
            return;
        }
        if (state === PANEL_EXPANDED && !classes.contains(PANEL_OPEN_CLASS)) {
            classes.add(PANEL_OPEN_CLASS);
            classes.remove(PANEL_COLLAPSED_CLASS);
        }
        else if (state === PANEL_COLLAPSED && !classes.contains(PANEL_COLLAPSED_CLASS)) {
            classes.add(PANEL_COLLAPSED_CLASS);
            classes.remove(PANEL_OPEN_CLASS);
        }
    }
}
